'use client'

import { useState, useEffect } from 'react'
import { supabase } from '../_utils/supabase'
import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../_components/_ui/background-beams'
import Link from 'next/link'
import Logo from '../_components/Logo'
import FieldToastIndicator from '../_components/_ui/field-toast-indicator'
import { FaGoogle } from 'react-icons/fa'

export default function LoginPage() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const router = useRouter()

    // Check if user is already logged in
    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.user) {
                // If user has username, redirect to dashboard
                if (session.user.user_metadata?.username) {
                    router.push(`/${session.user.user_metadata.username}/dashboard`)
                } else {
                    // If no username, redirect to username setup
                    router.push('/auth/username-setup')
                }
            }
        }

        checkAuth()
    }, [router])

    const inputClasses = `
        w-full px-4 py-2 rounded-md
        border border-gray-300 dark:border-gray-600
        focus:border-blue-500 focus:ring-2 focus:ring-blue-200
        bg-white dark:bg-gray-700
        text-gray-900 dark:text-gray-100
        placeholder-gray-400 dark:placeholder-gray-500
    `

    const isEmailValid = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleLogin = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError('')
        setSuccessMessage('')

        try {
            const { data, error: loginError } = await supabase.auth.signInWithPassword({
                email,
                password,
            })

            if (loginError) {
                setError(getErrorMessage(loginError))
                return
            }

            setSuccessMessage('Login successful! Redirecting...')
            const username = data.user.user_metadata.username
            router.push(`/${username}/dashboard/`)
        } catch (error) {
            console.error('Unexpected error:', error)
            setError('An unexpected error occurred during login')
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleLogin = async () => {
        try {
            setLoading(true)
            setError('')
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`
                }
            })
            if (error) throw error
        } catch (error) {
            setError('Error connecting to Google')
            console.error('Google login error:', error)
        } finally {
            setLoading(false)
        }
    }

    const getErrorMessage = (error) => {
        if (error.message.includes('Invalid login credentials')) {
            return 'Invalid email or password. Please try again.'
        } else if (error.message.includes('Email not confirmed')) {
            return 'Please confirm your email address before logging in.'
        } else if (error.message.includes('Invalid email')) {
            return 'Please enter a valid email address.'
        } else if (error.message.includes('Rate limit exceeded')) {
            return 'Too many login attempts. Please try again later.'
        } else if (error.message.includes('User not found')) {
            return 'No account found with this email. Please sign up.'
        } else if (error.message.includes('Email link is invalid or has expired')) {
            return 'The login link is invalid or has expired. Please request a new one.'
        } else {
            return `Error: ${error.message}`
        }
    }

    return (
        <div className="min-h-screen w-full relative flex items-center justify-center md:justify-end px-4">
            <BackgroundBeams className="opacity-40" />
            
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md p-8 mx-auto md:mx-12 my-10 
                         bg-white dark:bg-gray-800 rounded-2xl
                         border border-gray-200 dark:border-gray-700 shadow-xl"
            >
                <Link href="/" className="block mb-8">
                    <Logo className="mx-auto" />
                </Link>

                <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
                    Welcome Back
                </h1>

                <div className="mb-6">
                    <button
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 
                                 border border-gray-300 dark:border-gray-600 rounded-lg
                                 text-gray-700 dark:text-gray-200 font-medium
                                 hover:bg-gray-50 dark:hover:bg-gray-700
                                 transition-colors duration-200"
                    >
                        <FaGoogle className="text-xl" />
                        Continue with Google
                    </button>
                </div>

                <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                            Or continue with email
                        </span>
                    </div>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputClasses}
                            required
                        />
                        {email && (
                            <FieldToastIndicator
                                visible={true}
                                message={isEmailValid() ? "Valid email format" : "Invalid email format"}
                                backgroundColor={isEmailValid() ? "#059669" : "#374151"}
                            />
                        )}
                    </div>

                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                            Password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={inputClasses}
                            required
                        />
                    </div>

                    {error && (
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-red-500 dark:text-red-400 text-sm text-center"
                        >
                            {error}
                        </motion.p>
                    )}

                    {successMessage && (
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-green-500 dark:text-green-400 text-sm text-center"
                        >
                            {successMessage}
                        </motion.p>
                    )}

                    <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3 px-4 rounded-lg 
                            ${loading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-blue-500 to-teal-400 cursor-pointer'}
                            text-white font-medium shadow-lg
                            transition-all duration-200`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Logging in...
                            </span>
                        ) : (
                            'Login'
                        )}
                    </motion.button>

                    <div className="z-[10000] text-center space-y-2">
                        <p className="text-gray-600 dark:text-gray-400">
                            Don't have an account?{' '}
                            <Link href="/sign-up" className="text-blue-500 dark:text-blue-400 z-50 hover:text-blue-600 dark:hover:text-blue-300">
                                Sign Up
                            </Link>
                        </p>
                        <Link href="/forgot-password" className="block text-sm z-10 text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                            Forgot your password?
                        </Link>
                    </div>
                </form>
            </motion.div>
        </div>
    )
}
